import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Clock, MapPin, Users, Share2, Radio, ArrowLeft, Check, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Navbar } from "@/components/Navbar";
import { AddToCalendar } from "@/components/AddToCalendar";
import { LikeButton } from "@/components/LikeButton";
import { CommentSection } from "@/components/CommentSection";
import { LivestreamPlayer } from "@/components/LivestreamPlayer";
import { useAuth } from "@/context/AuthContext";
import { useLang, catLabel } from "@/lib/i18n";
import { toHijri } from "@/lib/hijri";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function EventDetail() {
  const { slug, eventId } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [assoc, setAssoc] = useState(null);
  const [event, setEvent] = useState(null);
  const [notifyingLive, setNotifyingLive] = useState(false);

  useEffect(() => {
    api.get(`/associations/${slug}`).then(({ data }) => setAssoc(data)).catch(() => {});
    api.get(`/associations/${slug}/events/${eventId}`).then(({ data }) => setEvent(data)).catch(() => {});
  }, [slug, eventId]);

  if (!assoc || !event) return <div className="min-h-screen bg-background" />;

  const d = new Date(event.start);
  const endD = event.end ? new Date(event.end) : null;
  const hijri = toHijri(event.start, assoc.hijri_offset || 0);
  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const isFull = event.capacity && event.registrations_count >= event.capacity && !event.is_registered;

  const toggleRegister = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post(`/associations/${slug}/events/${eventId}/register`);
      setEvent((e) => ({ ...e, is_registered: data.registered, registrations_count: data.count }));
      toast.success(data.registered ? t("registered") : t("unregister_event"));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: event.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t("link_copied"));
    }
  };

  const banners = event.images?.length ? event.images : (event.image ? [event.image] : []);
  const canNotifyLive = Boolean(user && event.livestream_url && (
    user.role === "super_admin" ||
    (user.association_slug === slug && (
      ["association_admin", "moderator"].includes(user.role) ||
      (user.role === "mosque_admin" && user.mosque_ids?.includes(event.mosque_id))
    ))
  ));

  const notifyLive = async () => {
    setNotifyingLive(true);
    try {
      const { data } = await api.post(`/associations/${slug}/events/${eventId}/notify-live`);
      toast.success(t("live_notified").replace("{count}", data.notified));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setNotifyingLive(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar assoc={assoc} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="event-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>

        {banners.length > 1 ? (
          <Carousel className="mb-6" data-testid="event-banner-carousel">
            <CarouselContent>
              {banners.map((img, i) => (
                <CarouselItem key={`${img}-${i}`}>
                  <div className="rounded-xl overflow-hidden border border-border">
                    <img src={img} alt={`${event.title} ${i + 1}`} className="w-full h-64 object-cover" />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-3" data-testid="event-banner-prev" />
            <CarouselNext className="right-3" data-testid="event-banner-next" />
          </Carousel>
        ) : banners[0] && (
          <div className="rounded-xl overflow-hidden border border-border mb-6">
            <img src={banners[0]} alt={event.title} className="w-full h-64 object-cover" />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="uppercase text-[10px] tracking-wide">{catLabel(t, event.category)}</Badge>
          {event.mosque_name && (
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: event.mosque_color }} />
              <Link to={`/${slug}/mosques/${event.mosque_id}`} className="hover:underline">{event.mosque_name}</Link>
            </span>
          )}
        </div>

        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mt-4" data-testid="event-title">{event.title}</h1>

        <div className="border border-border rounded-lg bg-card p-5 mt-6 grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] font-mono-data uppercase tracking-wider text-muted-foreground">{t("date")}</p>
            <p className="font-heading font-bold capitalize mt-1" data-testid="event-gregorian-date">
              {d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
            <p className="text-sm font-mono-data text-muted-foreground" data-testid="event-hijri-date">{hijri.label} AH</p>
          </div>
          <div>
            <p className="text-[11px] font-mono-data uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{t("start_time")}</p>
            <p className="font-mono-data font-medium mt-1">
              {d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
              {endD && ` – ${endD.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`}
            </p>
            {(event.location || event.mosque_name) && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" />{event.location || event.mosque_name}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            data-testid="event-register-btn"
            onClick={toggleRegister}
            disabled={isFull}
            variant={event.is_registered ? "secondary" : "default"}
            className="rounded-full active:scale-95"
          >
            {event.is_registered ? <><Check className="h-4 w-4 mr-2" />{t("registered")}</> : isFull ? t("full") : t("register_event")}
          </Button>
          <AddToCalendar event={event} slug={slug} />
          <Button variant="outline" data-testid="event-share-btn" onClick={share} className="rounded-full active:scale-95">
            <Share2 className="h-4 w-4 mr-2" /> {t("share")}
          </Button>
          {event.livestream_url && (
            <Button variant="outline" data-testid="event-livestream-btn" onClick={() => window.open(event.livestream_url, "_blank")} className="rounded-full active:scale-95 text-red-500 border-red-500/40">
              <Radio className="h-4 w-4 mr-2" /> {t("livestream")}
            </Button>
          )}
          {canNotifyLive && (
            <Button data-testid="notify-live-btn" onClick={notifyLive} disabled={notifyingLive} className="rounded-full active:scale-95 bg-red-600 hover:bg-red-700 text-white">
              <Radio className="h-4 w-4 mr-2" /> {notifyingLive ? "..." : t("notify_live")}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4">
          <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="event-participants">
            <Users className="h-4 w-4" /> {event.registrations_count} {t("participants")}
            {event.capacity ? ` / ${event.capacity}` : ""}
          </p>
          <LikeButton targetType="event" targetId={event.id} liked={event.liked} count={event.likes_count} />
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1.5" data-testid="reminder-hint">
          <BellRing className="h-3 w-3 text-accent" /> {t("reminder_hint")}
        </p>

        <LivestreamPlayer url={event.livestream_url} />

        {event.description && (
          <div className="mt-8 border-t border-border pt-6">
            <p className="leading-relaxed whitespace-pre-wrap" data-testid="event-description">{event.description}</p>
          </div>
        )}

        <div className="mt-8 border-t border-border pt-6 pb-4">
          <h2 className="font-heading font-bold mb-4">{t("comments")}</h2>
          <CommentSection targetType="event" targetId={event.id} />
        </div>
      </div>
    </div>
  );
}
